/**
 * Whether the organization shares a tracker's sign-in (§5.21, §5.25), on the
 * Jira and Trello pages of its integrations: who set it and when, never a
 * value. The credential is two organization secrets under reserved names
 * (`@airlock/contracts/cloud/shared-integrations`), sealed on an owner's or an
 * admin's machine — this page cannot set one, only say where it is set, and
 * Secrets is where it can be removed. Jira's names its site in a third, sealed
 * the same way; one shared before that names none, and members' machines do
 * not use it, which the note says.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import type { OrgSecretList, OrgSecretPresence } from '@airlock/contracts/cloud/org-secrets';
import { SHARED_INTEGRATION_SECRETS, type SharedIntegrationKind } from '@airlock/contracts/cloud/shared-integrations';
import { Note } from '../../components/list';
import { api, apiPath } from '../../lib/api';
import { isoDay } from '../../lib/format';

const FILTER: Record<SharedIntegrationKind, string> = {
  jira: 'Each member imports the tickets assigned to their own address; one Jira does not know imports the mapped projects’ open tickets.',
  trello: 'Trello does not show a member’s address, so each member imports the mapped boards’ open cards.'
};

export function SharedSignInNote({ orgId, kind }: { orgId: string; kind: SharedIntegrationKind }): React.JSX.Element | null {
  const [list, setList] = useState<OrgSecretList | null>(null);
  useEffect(() => {
    let live = true;
    void api
      .get<OrgSecretList>(apiPath`/orgs/${orgId}/org-secrets`)
      .then((answer) => live && setList(answer))
      .catch(() => live && setList(null));
    return () => {
      live = false;
    };
  }, [orgId]);
  if (!list?.available) return null;
  const names = SHARED_INTEGRATION_SECRETS[kind];
  const set: OrgSecretPresence | undefined = list.secrets.find((one) => one.name === names.token);
  const unbound = !!set && 'site' in names && !list.secrets.some((one) => one.name === names.site);
  const provider = kind === 'jira' ? 'Jira' : 'Trello';
  return (
    <div style={{ paddingTop: 14 }}>
      <Note tone={set && !unbound ? 'teal' : 'plain'}>
        {unbound
          ? `A shared ${provider} sign-in was set by ${set.updatedBy.label} on ${isoDay(set.updatedAt)} before Airlock sealed a site with it, so members’ machines do not use it. An owner or admin shares it again from Airlock: Settings › Integrations › ${provider}, naming the site.`
          : set
          ? `A shared ${provider} sign-in is set by ${set.updatedBy.label} on ${isoDay(set.updatedAt)}. A member with no sign-in of their own uses it; their own always wins. ${FILTER[kind]}`
          : `An owner or admin can share one ${provider} sign-in with every member who has none, from Airlock: Settings › Integrations › ${provider}. It is sealed on their machine — this page never holds it.`}
      </Note>
    </div>
  );
}
